import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../../lib/utils";
import { calculatePosition } from "./positioningUtils";

interface WebViewPopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WebViewPopover: React.FC<WebViewPopoverProps> = ({
  children,
  content,
  side = "bottom",
  sideOffset = 8,
  className,
  open: controlledOpen,
  onOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDialogElement>(null);

  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open);
      } else {
        setInternalOpen(open);
      }
    },
    [onOpenChange]
  );

  const updatePosition = () => {
    if (!triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    // If content hasn't been rendered yet (width/height are 0), use estimated dimensions
    const contentWidth = contentRect.width || 200;
    const contentHeight = contentRect.height || 100;

    const result = calculatePosition({
      side,
      sideOffset,
      triggerRect,
      contentWidth,
      contentHeight,
      padding: 16,
      narrowBreakpoint: 768
    });

    setPosition(result);
    setIsPositioned(true);
  };

  useEffect(() => {
    if (isOpen) {
      setIsPositioned(false); // Reset positioning state

      // Use multiple attempts to ensure content is rendered
      const attemptPositioning = () => {
        if (contentRef.current) {
          const rect = contentRef.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            // Content is properly rendered, update position
            updatePosition();
          } else {
            // Content not ready yet, try again
            setTimeout(attemptPositioning, 10);
          }
        }
      };

      // Start positioning attempts
      requestAnimationFrame(attemptPositioning);

      const handleResize = () => updatePosition();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    } else {
      setIsPositioned(false); // Reset when closed
    }
  }, [isOpen, side, sideOffset]);

  // Attach click and mousedown handlers imperatively to the trigger element
  // to avoid SonarQube warnings about interactive handlers on non-interactive elements
  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const handleTriggerClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(!isOpen);
    };

    const handleTriggerMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    trigger.addEventListener("click", handleTriggerClick);
    trigger.addEventListener("mousedown", handleTriggerMouseDown);

    return () => {
      trigger.removeEventListener("click", handleTriggerClick);
      trigger.removeEventListener("mousedown", handleTriggerMouseDown);
    };
  }, [isOpen, setIsOpen]);

  // Handle clicks outside to close and manage dialog overlay pointer events
  useEffect(() => {
    if (!isOpen) return;

    // Only disable pointer events on dialog overlays (not content) when popover is open
    const dialogOverlays = document.querySelectorAll(
      "[data-radix-dialog-overlay]"
    );

    dialogOverlays.forEach((overlay) => {
      (overlay as HTMLElement).style.pointerEvents = "none";
    });

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const targetElement = target as HTMLElement;

      // Don't close if clicking within the popover content
      if (contentRef.current?.contains(target)) {
        return;
      }

      // Close if clicking on another date picker trigger
      if (targetElement.closest('[data-eds-date-picker="true"]')) {
        setIsOpen(false);
        return;
      }

      // Close if clicking outside both trigger and content
      if (
        triggerRef.current &&
        contentRef.current &&
        !triggerRef.current.contains(target) &&
        !contentRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    // WCAG 2.1.1 – allow keyboard dismissal via Escape and return focus
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    // Stop propagation on content mousedown to prevent dialog interference
    const contentEl = contentRef.current;
    const handleContentMouseDown = (e: MouseEvent) => e.stopPropagation();
    contentEl?.addEventListener("mousedown", handleContentMouseDown);

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      contentEl?.removeEventListener("mousedown", handleContentMouseDown);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);

      // Restore pointer events when popover closes
      dialogOverlays.forEach((overlay) => {
        (overlay as HTMLElement).style.pointerEvents = "";
      });
    };
  }, [isOpen, setIsOpen]);

  return (
    <>
      <div
        ref={triggerRef}
        style={{
          cursor: "pointer",
          position: "relative",
          zIndex: 1,
          background: "none",
          border: "none",
          padding: 0
        }}
      >
        {children}
      </div>

      {isOpen &&
        createPortal(
          <dialog
            ref={contentRef}
            open
            aria-label="Popover dropdown"
            className={cn(
              "fixed rounded-md bg-popover text-popover-foreground shadow-md outline-none",
              "webview-popover-dropdown", // Custom class for styling
              isPositioned ? "animate-in fade-in-0 zoom-in-95" : "opacity-0",
              className
            )}
            // UA reset — duplicated in eds-component-overrides.css as a specificity safety net
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              minWidth: "200px",
              maxWidth: window.innerWidth < 768 ? "90vw" : "300px",
              width: "auto",
              zIndex: 2147483647,
              boxShadow:
                "var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
              position: "fixed",
              pointerEvents: "auto",
              margin: 0,
              padding: 0,
              right: "auto",
              bottom: "auto",
              border: "none"
            }}
          >
            {content}
          </dialog>,
          document.body
        )}
    </>
  );
};
